import { HttpParams } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Observable, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { ReportFileType, SalesReportChartUnit, SalesReportViewType } from 'src/app/enum';
import { LxmDialog } from 'src/app/_helpers';
import {
  formatReportDay,
  toReportDay,
  toReportDayDate,
  toReportDayKey
} from 'src/app/cards/reports/reports-date.utils';
import { SalesReportExportDialog } from 'src/app/cards/reports/sales-report/sales-report-export/sales-report-export.dialog';
import {
  IExportSalesReportRequest,
  IGetSalesReportEntriesRequest,
  ISalesReportDayMetric,
  ISalesReportRow,
  ISalesReportSearchData,
  ReportsService
} from 'src/app/_services/reports.service';

const PAGE_SIZE_OPTIONS = [4, 8];
const DEFAULT_PAGE_SIZE = 4;
const BAR_MAX_HEIGHT = 100;
const SCALE_TICK_COUNT = 3;
const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
const MAX_DAYS = 35;

export interface ISalesReportChartDay {
  date: string;
  label: string;
  monthLabel: string;
  showMonth: boolean;
  isToday: boolean;
}

export interface ISalesReportChartRow {
  id: string;
  name: string;
  ean: string;
  code: string;
  gln: string;
  unit: string;
  categories: string[];
  locationFormat?: { [lang: string]: string };
  showImage: boolean;
  scaleTicks: number[];
  dayValues: ISalesReportDayValue[];
}

export interface ISalesReportDayValue {
  sales: number;
  inventory: number;
  revenue: number;
  averagePrice: number | null;
  salesLabel: string;
  inventoryLabel: string;
  salesHeight: number;
  inventoryHeight: number;
}

export interface ISalesReportChartTooltip {
  visible: boolean;
  left: number;
  top: number;
  arrowLeft: number;
  above: boolean;
  dateLabel: string;
  sold: number;
  inventory: number;
  revenue: number;
  averagePrice: number | null;
  unit: string;
}

@Component({
  selector: 'sales-report-products',
  templateUrl: './sales-report-products.component.html',
  host: { class: 'sales-report-products' },
  styleUrls: ['./sales-report-products.component.scss']
})
export class SalesReportProductsComponent implements OnInit, OnDestroy {
  @Input() public search: Observable<ISalesReportSearchData>;
  @Output() public expand = new EventEmitter<void>();
  @Input() public retailerName = '';
  @Input() public dateFrom: Date | null;
  @Input() public dateTo: Date | null;
  @Input() public expiresAt: Date | null;
  @Input() public isExpired = false;
  @Input() public canManage = false;

  public reportId: string | null;
  public rows: ISalesReportChartRow[] = [];
  public days: ISalesReportChartDay[] = [];
  public viewType: SalesReportViewType = SalesReportViewType.ProductByDay;

  public count = 0;
  public pageIndex = 0;
  public pageSize = DEFAULT_PAGE_SIZE;
  public pageSizeOptions = PAGE_SIZE_OPTIONS;
  public orderBy = 'name';

  public isFetching = false;
  public showSales = true;
  public showInventory = true;
  public chartUnit = SalesReportChartUnit.Piece;
  public SalesReportChartUnit = SalesReportChartUnit;

  public hoveredDayIndex: number | null = null;
  public tooltip: ISalesReportChartTooltip = {
    visible: false,
    left: 0,
    top: 0,
    arrowLeft: 16,
    above: false,
    dateLabel: '',
    sold: 0,
    inventory: 0,
    revenue: 0,
    averagePrice: null,
    unit: ''
  };

  private _searchData: ISalesReportSearchData | null;
  private _items: ISalesReportRow[] = [];
  private _searchSub: Subscription;
  private _entriesSub: Subscription;

  @ViewChild('download') private _download: ElementRef;

  constructor(
    private _route: ActivatedRoute,
    private _reportsService: ReportsService,
    private _dialog: LxmDialog
  ) {
    this.reportId = this._route.snapshot.paramMap.get('reportId');
  }

  public ngOnInit() {
    if (this.search) {
      this._searchSub = this.search.subscribe((data) => {
        if (!data) {
          return;
        }

        this._searchData = data;
        this.viewType = data.viewType || SalesReportViewType.ProductByDay;
        this.showSales = this.hasFileType(ReportFileType.Selver_SalesReport_SalesFile);
        this.showInventory = this.hasFileType(ReportFileType.Selver_SalesReport_InventoryFile);
        this.pageIndex = 0;
        this.loadEntries();
      });
    }
  }

  public ngOnDestroy() {
    this._searchSub?.unsubscribe();
    this._entriesSub?.unsubscribe();
  }

  public onPage(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadEntries();
  }

  public toggleSort() {
    this.orderBy = this.orderBy === 'name' ? 'name desc' : 'name';
    this.pageIndex = 0;
    this.loadEntries();
  }

  public setChartUnit(unit: SalesReportChartUnit) {
    if (this.chartUnit === unit) {
      return;
    }

    this.chartUnit = unit;
    this.hideTooltip();
    this.redrawChart();
  }

  public exportXls() {
    if (!this.reportId || !this._searchData) {
      return;
    }

    this._dialog.open(SalesReportExportDialog, {
      resolve: () =>
        this._reportsService
          .exportXls(this.reportId, this.toExportRequest())
          .pipe(
            mergeMap(async (res) => {
              const blob = await this._reportsService.downloadXls(res.id).toPromise();
              return {
                fileName: res.fileName,
                blob
              };
            })
          )
          .pipe(
            map((res) => {
              const url = window.URL.createObjectURL(res.blob);
              const link = this._download.nativeElement;
              link.href = url;
              link.download = res.fileName;
              link.click();
              window.URL.revokeObjectURL(url);
            })
          )
    });
  }

  public hideTooltip() {
    this.tooltip.visible = false;
    this.hoveredDayIndex = null;
  }

  public get isProductView(): boolean {
    return this.viewType === SalesReportViewType.ProductByDay;
  }

  public get deletedAt(): Date | null {
    if (!this.expiresAt) {
      return null;
    }
    return new Date(this.expiresAt.getTime() + 12 * 60 * 60 * 1000);
  }

  public get showAveragePrice(): boolean {
    return this.isProductView || this.hasSelectedProduct();
  }

  public get emptyHeadingKey(): string {
    return this.isProductView
      ? 'cards.reports.sales_report.no_entries_heading'
      : 'cards.reports.sales_report.no_entries_heading_stores';
  }

  public showDayTooltip(
    event: MouseEvent,
    row: ISalesReportChartRow,
    index: number
  ) {
    const day = row.dayValues[index];
    if (!day) {
      this.hideTooltip();
      return;
    }

    this.hoveredDayIndex = index;
    const position = this.tooltipPosition(event.clientX, event.clientY);
    this.tooltip = {
      visible: true,
      ...position,
      dateLabel: formatReportDay(this.days[index]?.date),
      sold: day.sales,
      inventory: day.inventory,
      revenue: day.revenue,
      averagePrice: day.averagePrice,
      unit: row.unit
    };
  }

  private tooltipPosition(clientX: number, clientY: number) {
    const width = 280;
    const height = 200;
    const margin = 8;
    const cursorGap = 16;
    const arrowNudge = 18;

    let left = clientX - arrowNudge;
    const maxLeft = window.innerWidth - width - margin;
    left = Math.min(Math.max(left, margin), Math.max(margin, maxLeft));

    let top = clientY + cursorGap;
    let above = false;
    if (top + height > window.innerHeight - margin) {
      top = Math.max(margin, clientY - height - 8);
      above = top < clientY;
    }

    const arrowLeft = Math.min(Math.max(12, clientX - left), width - 12);

    return { left, top, arrowLeft, above };
  }

  private loadEntries() {
    if (!this.reportId || !this._searchData) {
      return;
    }

    const from = toReportDayKey(this._searchData.from);
    const to = toReportDayKey(this._searchData.to);
    this.days = this.buildDays(from, to);

    const params = new HttpParams()
      .set('$top', String(this.pageSize))
      .set('$skip', String(this.pageIndex * this.pageSize))
      .set('$count', 'true')
      .set('$orderBy', this.orderBy);

    this.isFetching = true;
    this._entriesSub?.unsubscribe();
    this._entriesSub = this._reportsService
      .getSalesReportEntries(
        this.reportId,
        this.toEntriesRequest(),
        params
      )
      .subscribe({
        next: (response) => {
          const items = response?.items || [];
          this._items = items;
          this.count = response?.count || items.length;
          const metricDays = this.daysFromMetrics(items);
          if (metricDays.length) {
            this.days = metricDays;
          }
          this.redrawChart();
          this.isFetching = false;
        },
        error: () => {
          this.count = 0;
          this._items = [];
          this.rows = [];
          this.isFetching = false;
        }
      });
  }

  private redrawChart() {
    this.rows = this._items.map((item) => this.toChartRow(item));
  }

  private toEntriesRequest(): IGetSalesReportEntriesRequest {
    return {
      id: this.reportId,
      viewType: this.viewType,
      from: toReportDayDate(this._searchData?.from),
      to: toReportDayDate(this._searchData?.to),
      locationIds: this._searchData?.locationIds,
      productIds: this._searchData?.productIds,
      fileTypes: this._searchData?.fileTypes,
      searchTerm: this._searchData?.searchTerm,
      inventoryFilter: this._searchData?.inventoryFilter,
      priceType: this._searchData?.priceType
    };
  }

  private toExportRequest(): IExportSalesReportRequest {
    return {
      ...this.toEntriesRequest(),
      orderBy: this.orderBy
    };
  }

  private toChartRow(item: ISalesReportRow): ISalesReportChartRow {
    const metrics = this.metricsByDay(item);
    const isEuro = this.chartUnit === SalesReportChartUnit.Euro;

    let maxSales = 0;
    let maxInventory = 0;

    const quantities = this.days.map((day) => {
      const metric = metrics[day.date];
      const sales = this.quantity(metric?.soldQuantity);
      const inventory = this.showInventory ? this.quantity(metric?.actualQuantity) : 0;
      const revenue = this.quantity(metric?.totalRevenue);
      const averagePrice = sales ? revenue / sales : null;
      const displaySales = isEuro ? revenue : sales;
      const displayInventory = isEuro ? inventory * (averagePrice || 0) : inventory;

      if (displaySales > maxSales) {
        maxSales = displaySales;
      }
      if (displayInventory > maxInventory) {
        maxInventory = displayInventory;
      }

      return {
        sales,
        inventory,
        revenue,
        averagePrice,
        displaySales,
        displayInventory
      };
    });

    const scaleTicks = this.niceScaleTicks(Math.max(maxSales, maxInventory));
    const scaleTop = scaleTicks[0] || 0;

    const dayValues = quantities.map((x) => ({
      sales: x.sales,
      inventory: x.inventory,
      revenue: x.revenue,
      averagePrice: x.averagePrice,
      salesLabel: this.formatBarLabel(x.displaySales),
      inventoryLabel: this.formatBarLabel(x.displayInventory),
      salesHeight: scaleTop ? Math.max(0, (x.displaySales / scaleTop) * BAR_MAX_HEIGHT) : 0,
      inventoryHeight: scaleTop ? Math.max(0, (x.displayInventory / scaleTop) * BAR_MAX_HEIGHT) : 0
    }));

    return {
      id: item.id,
      name: item.name,
      ean: item.productPrimaryEan || '',
      code: item.productPrimaryCode || '',
      gln: item.locationGln || '',
      unit: this.isProductView || this.showInventory
        ? (this.firstMetric(item)?.unitType || '')
        : '',
      categories: item.productGs1SegmentName ? [item.productGs1SegmentName] : [],
      locationFormat: item.locationFormat,
      showImage: this.isProductView,
      scaleTicks,
      dayValues
    };
  }

  private niceScaleTicks(maxValue: number): number[] {
    if (maxValue <= 0) {
      return Array(SCALE_TICK_COUNT).fill(0);
    }

    const step = this.niceCeil(maxValue / SCALE_TICK_COUNT);
    return Array.from(
      { length: SCALE_TICK_COUNT },
      (_, i) => step * (SCALE_TICK_COUNT - i)
    );
  }

  private niceCeil(value: number): number {
    if (value <= 0) {
      return 0;
    }

    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    const residual = value / magnitude;
    const nice = NICE_STEPS.find((n) => n + 1e-9 >= residual) || 10;
    return nice * magnitude;
  }

  public scaleTickPercent(index: number): number {
    return (index / SCALE_TICK_COUNT) * 100;
  }

  public formatScaleTick(value: number): string {
    if (!value) {
      return '0';
    }
    if (value >= 100 || Number.isInteger(value)) {
      return String(Math.round(value));
    }
    return String(Math.round(value * 10) / 10);
  }

  private formatBarLabel(value: number): string {
    return String(Math.round(value));
  }

  private metricsByDay(item: ISalesReportRow) {
    const mapped: { [date: string]: ISalesReportDayMetric } = {};
    const metrics = item.metrics || {};

    Object.keys(metrics).forEach((key) => {
      const metric = metrics[key];
      const dateKey = toReportDayKey(metric?.date || key);
      if (dateKey) {
        mapped[dateKey] = metric;
      }
    });

    return mapped;
  }

  private firstMetric(item: ISalesReportRow): ISalesReportDayMetric | null {
    const metrics = item.metrics || {};
    const firstKey = Object.keys(metrics)[0];
    return firstKey ? metrics[firstKey] : null;
  }

  private daysFromMetrics(items: ISalesReportRow[]) {
    const dates = new Set<string>();

    items.forEach((item) => {
      const metrics = item.metrics || {};
      Object.keys(metrics).forEach((key) => {
        const dateKey = toReportDayKey(metrics[key]?.date || key);
        if (dateKey) {
          dates.add(dateKey);
        }
      });
    });

    const today = toReportDayKey(new Date());
    return Array.from(dates)
      .sort()
      .slice(0, MAX_DAYS)
      .map((date, index, all) => this.toDayHeader(date, all[index - 1], today || undefined));
  }

  private hasSelectedProduct() {
    return !!(this._searchData?.productIds && this._searchData.productIds.length);
  }

  private hasFileType(fileType: ReportFileType) {
    const types = this._searchData?.fileTypes || [];
    if (!types.length) {
      return true;
    }

    return types.includes(fileType);
  }

  private quantity(value: number | undefined) {
    return typeof value === 'number' && !isNaN(value) ? value : 0;
  }

  private buildDays(from: string | null, to: string | null) {
    const start = toReportDay(from);
    const end = toReportDay(to);
    if (!start || !end || end.isBefore(start)) {
      return [];
    }

    const today = toReportDayKey(new Date()) || undefined;
    const days: ISalesReportChartDay[] = [];
    const cursor = start.clone();

    while (cursor.isSameOrBefore(end) && days.length < MAX_DAYS) {
      const date = toReportDayKey(cursor);
      if (!date) {
        break;
      }

      const previous = days.length ? days[days.length - 1].date : undefined;
      days.push(this.toDayHeader(date, previous, today));
      cursor.add(1, 'day');
    }

    return days;
  }

  private toDayHeader(
    date: string,
    previousDate?: string,
    today?: string
  ): ISalesReportChartDay {
    const day = toReportDay(date);
    const previous = toReportDay(previousDate);

    return {
      date,
      label: String(day?.date() ?? ''),
      monthLabel: day?.format('MMMM') ?? '',
      showMonth: !previous || !day?.isSame(previous, 'month'),
      isToday: date === (today || toReportDayKey(new Date()))
    };
  }
}
